import {
  useMutation,
  useQueryClient,
} from "@tanstack/react-query"

import { api } from "@/api"
import { ENDPOINTS } from "@/constants"
import { useAuth } from "@/contexts/auth"
import { toast } from "@/hooks/use-toast"
import { getApiErrorMessage } from "@/lib/api-error"

export type UpsertRepicConfigurationRequest = {
  id?: string
  lastAction: "PAYMENT_METHOD_CHANGE" | "CANCEL"
  paymentMethod: "CREDIT_CARD" | "PIX" | "BOLETO"
  retryConfigurations?: { count: number; day: number }[]
}

export const upsertRepicConfiguration = async (
  { id, ...body }: UpsertRepicConfigurationRequest,
  whiteLabelUuid: string
) => {
  if (id) {
    const url = `${ENDPOINTS.REPIC_CONFIGURATION}/${id}`
    const { data } = await api.put(url, body, {
      headers: {
        "x-white-label-uuid": whiteLabelUuid,
      },
    })
    return data
  }

  const { data } = await api.post(
    ENDPOINTS.REPIC_CONFIGURATION,
    body,
    {
      headers: {
        "x-white-label-uuid": whiteLabelUuid,
      },
    }
  )
  return data
}

export const useUpsertRepicConfiguration = () => {
  const queryClient = useQueryClient()
  const { selectedWhiteLabel } = useAuth()
  const whitelabel_ids = selectedWhiteLabel?.uuid

  return useMutation<
    unknown,
    unknown,
    UpsertRepicConfigurationRequest
  >({
    mutationFn: async (
      payload: UpsertRepicConfigurationRequest
    ) =>
      upsertRepicConfiguration(
        payload,
        whitelabel_ids as string
      ),
    onSuccess: () => {
      toast({
        title: "Configuração salva com sucesso",
        variant: "success",
      })

      queryClient.invalidateQueries({
        queryKey: [ENDPOINTS.REPIC_CONFIGURATION],
      })
      queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey[0] ===
          ENDPOINTS.REPIC_CONFIGURATION,
      })
    },
    onError: (error: unknown) => {
      toast({
        title: getApiErrorMessage(
          error,
          "Erro ao salvar configuração"
        ),
        variant: "destructive",
      })
    },
  })
}

export default useUpsertRepicConfiguration
